import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import Tooltip from "@mui/material/Tooltip";

import MarkAsApproved from "./MarkAsApproved";
import { fetchPersonalDocumentTypes } from "../service";
import LoadingSpinner from "./LoadingSpinner";
import { theme } from "../../../theme";
import PersonalDocumentsFileAndUploader from "./personalDocuments/PersonalDocumentsFileAndUploader";
import CardTemplate from "./template/CardTemplate";
import { timestampToDate } from "../../../utils";

const PersonalDocumentsApproved = ({ isHistory = false }) => {
    const { ftm_id } = useParams();

    const [loading, setLoading] = useState(true);
    const [personalDocumentTypes, setPersonalDocumentTypes] = useState([]);
    const [openFileUploadModal, setOpenFileUploadModal] = useState(false);
    const [selectedDocumentType, setSelectedDocumentType] = useState(null);
    const [refreshCounter, setRefreshCounter] = useState(0);

    // 🔁 Fetch document types
    const getPersonalDocumentTypes = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetchPersonalDocumentTypes(ftm_id);
            const types = response?.status === "Success" ? response.types : [];
            setPersonalDocumentTypes(types);
        } catch (error) {
            console.error("Error fetching personal document types:", error);
            setPersonalDocumentTypes([]);
        } finally {
            setLoading(false);
        }
    }, [ftm_id]);

    useEffect(() => {
        getPersonalDocumentTypes();
    }, [getPersonalDocumentTypes, refreshCounter]);

    // 🔁 Common Handlers
    const handleCloseFileUploadModal = useCallback(() => {
        setOpenFileUploadModal(false);
    }, []);

    const handleRefresh = useCallback(() => {
        setRefreshCounter((prev) => prev + 1);
    }, []);

    const handleDocumentNavigation = useCallback(
        (id, direction) => {
            const index = personalDocumentTypes.findIndex(
                (item) => item.personal_document_type_id === id
            );
            if (index === -1) return;

            const newIndex =
                direction === "next"
                    ? index + 1
                    : direction === "prev"
                    ? index - 1
                    : index;

            if (personalDocumentTypes[newIndex]) {
                setSelectedDocumentType(personalDocumentTypes[newIndex]);
            }
        },
        [personalDocumentTypes]
    );

    const handleOpenFileUploadModal = useCallback((docType) => {
        setSelectedDocumentType(docType);
        setOpenFileUploadModal(true);
    }, []);

    // 🧩 Render: Single document item
    const renderDocumentItem = (item) => {
        const expiryDate =
            item?.personal_document?.ftm_personal_document_expiry_date
                ? timestampToDate(
                      item.personal_document.ftm_personal_document_expiry_date
                  )
                : "N/A";

        return (
            <li
                key={item.personal_document_type_id}
                className="list-group-item list-group-item-action border-0 d-flex justify-content-between align-items-start border-radius-0-2 cursor-pointer"
                onClick={() => handleOpenFileUploadModal(item)}
            >
                {/* Left Section */}
                <div className="d-flex flex-column w-100">
                    <h6
                        className={`mb-0 text-dark font-weight-bold text-${
                            isHistory ? "sm" : "lg"
                        }`}
                    >
                        <i
                            className={`material-icons opacity-10 text-${theme.main} me-2`}
                        >
                            {item.personal_document_type_icon}
                        </i>

                        {item.personal_document_type_name}

                        {item.personal_document_type_is_mandatory === 1 && (
                            <i className="material-icons text-md opacity-10 text-danger ms-2">
                                star
                            </i>
                        )}

                        {item.already_uploaded_personal_document && (
                            <i
                                className={`material-icons text-md opacity-10 text-${theme.main} ms-2`}
                            >
                                library_books
                            </i>
                        )}

                        {item.personal_document?.ftm_personal_document_comment && (
                            <Tooltip
                                title={
                                    item.personal_document
                                        ?.ftm_personal_document_comment
                                }
                                arrow
                                placement="right"
                            >
                                <i
                                    className={`material-icons text-md opacity-10 text-${theme.main} ms-2`}
                                >
                                    chat_bubble
                                </i>
                            </Tooltip>
                        )}
                    </h6>

                    {/* Expiry Date */}
                    <div className="d-flex align-items-center text-xs text-secondary mb-1">
                        <i
                            className={`material-icons text-sm opacity-70 text-${theme.main} me-1`}
                        >
                            alarm
                        </i>
                        <strong className="me-1">Expiry Date:</strong>
                        <span>{expiryDate}</span>
                    </div>

                    {/* Description */}
                    {item.personal_document_type_description && (
                        <span className="text-xs text-muted mb-0">
                            {item.personal_document_type_description}
                        </span>
                    )}
                </div>

                {/* Right Section */}
                <div className="d-flex align-items-center text-sm">
                    <i
                        className={`material-icons opacity-10 ms-2 ${
                            item.personal_document_is_approve
                                ? `text-${theme.main}`
                                : ""
                        }`}
                    >
                        {item.personal_document_is_approve
                            ? "verified"
                            : "add_circle"}
                    </i>
                </div>
            </li>
        );
    };

    // 🧠 Main Render
    return (
        <CardTemplate
            title="Personal Documents"
            isHistory={isHistory}
        >
            {loading ? (
                <LoadingSpinner loading={loading} />
            ) : personalDocumentTypes.length > 0 ? (
                <ul className="list-group mb-3">
                    {personalDocumentTypes.map(renderDocumentItem)}
                </ul>
            ) : (
                <p className="text-muted text-center my-3">
                    No personal document types available.
                </p>
            )}

            {!isHistory && (
                <MarkAsApproved approvalProcessKey="ALL_PERSONAL_DOCUMENTS_APPROVED" />
            )}

            <PersonalDocumentsFileAndUploader
                ftm_id={ftm_id}
                item={selectedDocumentType}
                items={personalDocumentTypes}
                show={openFileUploadModal}
                handleClose={handleCloseFileUploadModal}
                type="certificates"
                parentRefresh={handleRefresh}
                handleNextItem={(id) => handleDocumentNavigation(id, "next")}
                handlePrevItem={(id) => handleDocumentNavigation(id, "prev")}
            />
        </CardTemplate>
    );
};

export default PersonalDocumentsApproved;